/**
 * YagiChatBot の開閉・リセット・AI 結果確認・公開 API。
 */
import { computed, nextTick, ref, type Ref } from 'vue'
import { useYagiChatBotCompany } from '@/composables/format-edit/useYagiChatBotCompany'
import { useYagiChatBotQuestions } from '@/composables/format-edit/useYagiChatBotQuestions'
import {
  fieldValueAsString,
  findQuestionIndexById,
  formatYagiChatDate,
  trackYagiChatGtag,
  type CloseDialogOptions,
  type YagiAiResultCallbacks,
  type YagiChatFormValue,
  type YagiChatMessage,
  type YagiChatQuestion
} from '@/utils/format-edit/yagiChatBot'

export type UseYagiChatBotOptions = {
  modalName: Ref<string>
  questions: Ref<YagiChatQuestion[]>
  value: Ref<YagiChatFormValue>
  autoSkipFilled: Ref<boolean>
  chatType: Ref<string>
  welcomeMessage: Ref<string>
  emitInput: (value: YagiChatFormValue) => void
  emitComplete: (value?: YagiChatFormValue) => void
  emitClose: () => void
  getChatMessagesEl: () => HTMLElement | null
}

export function useYagiChatBot(options: UseYagiChatBotOptions) {
  const visible = ref(false)
  const currentQuestionIndex = ref(0)
  const chatMessages = ref<YagiChatMessage[]>([])
  const currentAnswer = ref('')
  const isComposing = ref(false)
  const showResult = ref(false)
  const result = ref<unknown>(null)
  const isLoadingCompanyInfo = ref(false)
  const autoFilledFields = ref<string[]>([])
  const allQuestionsCompleted = ref(false)
  const completeTracked = ref(false)
  const aiResultMode = ref(false)
  const aiResultCallbacks = ref<YagiAiResultCallbacks | null>(null)
  const thinkingMode = ref(false)
  const thinkingLabel = ref('考え中…')

  const questionsComputed = computed(() => options.questions.value)
  const valueComputed = computed(() => options.value.value)

  const currentQuestion = computed(() => {
    if (currentQuestionIndex.value < options.questions.value.length) {
      return options.questions.value[currentQuestionIndex.value]
    }
    return null
  })

  function scrollChatToBottom() {
    const el = options.getChatMessagesEl()
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }

  const questionsApi = useYagiChatBotQuestions({
    questions: questionsComputed,
    value: valueComputed,
    emitInput: options.emitInput,
    emitComplete: options.emitComplete,
    currentQuestionIndex,
    chatMessages,
    currentAnswer,
    autoFilledFields,
    allQuestionsCompleted,
    aiResultMode,
    aiResultCallbacks,
    thinkingMode,
    scrollChatToBottom
  })

  const companyApi = useYagiChatBotCompany({
    questions: questionsComputed,
    value: valueComputed,
    emitInput: options.emitInput,
    currentQuestionIndex,
    chatMessages,
    currentAnswer,
    autoFilledFields,
    aiResultMode,
    aiResultCallbacks,
    isLoadingCompanyInfo,
    addQuestionToChat: questionsApi.addQuestionToChat,
    moveToNextUnskippedQuestion: questionsApi.moveToNextUnskippedQuestion,
    scrollChatToBottom
  })

  function resetChat() {
    currentQuestionIndex.value = 0
    chatMessages.value = []
    currentAnswer.value = ''
    showResult.value = false
    result.value = null
    autoFilledFields.value = []
    allQuestionsCompleted.value = false
    completeTracked.value = false
    aiResultMode.value = false
    aiResultCallbacks.value = null
    thinkingMode.value = false
    thinkingLabel.value = '考え中…'

    if (options.welcomeMessage.value) {
      chatMessages.value.push({
        type: 'question',
        text: options.welcomeMessage.value
      })
    }
  }

  function open() {
    resetChat()
    visible.value = true

    trackYagiChatGtag('yagi_chat_open', {
      chat_type: options.chatType.value,
      total_questions: options.questions.value.length
    })

    if (options.autoSkipFilled.value) {
      questionsApi.skipFilledQuestions()
    } else {
      questionsApi.addQuestionToChat(0)
    }
  }

  function closeDialog(closeOptions?: CloseDialogOptions) {
    const suppressCloseEvent = !!(
      closeOptions && closeOptions.suppressCloseEvent
    )

    if (!showResult.value && !suppressCloseEvent) {
      trackYagiChatGtag('yagi_chat_close', {
        chat_type: options.chatType.value,
        completed: false,
        answered_questions: currentQuestionIndex.value
      })
    }

    visible.value = false
    showResult.value = false

    if (!suppressCloseEvent) {
      options.emitClose()
    }
  }

  /** VFM @input: overlay / Esc で閉じたとき。open 経由の true は無視 */
  function onVisibleInput(value: boolean) {
    if (value) {
      visible.value = true
      return
    }
    if (!visible.value) return
    closeDialog()
  }

  function showResultView(resultData: unknown) {
    result.value = resultData
    showResult.value = true
  }

  function showThinkingIndicator(label?: string) {
    thinkingMode.value = true
    thinkingLabel.value = label || '考え中…'
    nextTick(() => scrollChatToBottom())
  }

  function hideThinkingIndicator() {
    thinkingMode.value = false
  }

  function showAiResultConfirm(
    text: string,
    callbacks?: YagiAiResultCallbacks | null
  ) {
    chatMessages.value.forEach((m) => {
      if (m.type === 'ai-result') m.active = false
    })
    aiResultCallbacks.value = callbacks || null
    aiResultMode.value = true
    chatMessages.value.push({
      type: 'ai-result',
      text,
      active: true
    })
    nextTick(() => scrollChatToBottom())
  }

  function handleAiResultAccept() {
    chatMessages.value.push({ type: 'answer', text: 'はい' })
    const cb = aiResultCallbacks.value
    aiResultMode.value = false
    aiResultCallbacks.value = null
    if (cb && typeof cb.onAccept === 'function') {
      cb.onAccept()
    }
  }

  function handleAiResultRegenerate() {
    chatMessages.value.push({ type: 'answer', text: '再生成する' })
    const cb = aiResultCallbacks.value
    if (cb && typeof cb.onRegenerate === 'function') {
      cb.onRegenerate()
    }
  }

  function handleAiResultEdit() {
    chatMessages.value.push({ type: 'answer', text: '自分で直す' })
    const cb = aiResultCallbacks.value
    aiResultMode.value = false
    aiResultCallbacks.value = null
    if (cb && typeof cb.onEdit === 'function') {
      cb.onEdit()
    }
  }

  function resumeFromQuestionId(questionId: string) {
    const index = findQuestionIndexById(options.questions.value, questionId)
    if (index === -1) return
    aiResultMode.value = false
    aiResultCallbacks.value = null
    thinkingMode.value = false
    allQuestionsCompleted.value = false
    currentQuestionIndex.value = index
    currentAnswer.value = fieldValueAsString(
      options.value.value,
      options.questions.value[index].field
    )
    questionsApi.addQuestionToChat(index)
    nextTick(() => scrollChatToBottom())
  }

  function markCompleted() {
    if (completeTracked.value) return
    completeTracked.value = true
    trackYagiChatGtag('yagi_chat_complete', {
      chat_type: options.chatType.value,
      total_questions: options.questions.value.length,
      used_url_autofill: autoFilledFields.value.length > 0,
      autofilled_fields: autoFilledFields.value.length
    })
  }

  /** 親が chatMessages を直叩きしていた箇所の代替 */
  function pushChatMessage(message: YagiChatMessage) {
    chatMessages.value.push(message)
    nextTick(() => scrollChatToBottom())
  }

  function handleEnterKey(event: KeyboardEvent) {
    if (isComposing.value) return
    event.preventDefault()
    if (currentQuestion.value && currentQuestion.value.type === 'url') {
      companyApi.submitUrlAnswer()
    } else {
      questionsApi.submitAnswer()
    }
  }

  const chatDate = computed(() => formatYagiChatDate())

  return {
    visible,
    currentQuestionIndex,
    chatMessages,
    currentAnswer,
    isComposing,
    showResult,
    result,
    isLoadingCompanyInfo,
    allQuestionsCompleted,
    aiResultMode,
    thinkingMode,
    thinkingLabel,
    currentQuestion,
    chatDate,
    open,
    closeDialog,
    onVisibleInput,
    resetChat,
    showResultView,
    showThinkingIndicator,
    hideThinkingIndicator,
    showAiResultConfirm,
    handleAiResultAccept,
    handleAiResultRegenerate,
    handleAiResultEdit,
    resumeFromQuestionId,
    markCompleted,
    pushChatMessage,
    handleEnterKey,
    addQuestionToChat: questionsApi.addQuestionToChat,
    skipFilledQuestions: questionsApi.skipFilledQuestions,
    submitAnswer: questionsApi.submitAnswer,
    submitNoneAnswer: questionsApi.submitNoneAnswer,
    submitQuickReply: questionsApi.submitQuickReply,
    editSkippedQuestions: questionsApi.editSkippedQuestions,
    handleQuestionsComplete: questionsApi.handleQuestionsComplete,
    skipUrlQuestion: companyApi.skipUrlQuestion,
    submitUrlAnswer: companyApi.submitUrlAnswer,
    editCompanyInfo: companyApi.editCompanyInfo
  }
}
